import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { fmtDate, fmtTime, inr, todayISO } from "@/lib/tps";
import { StatusBadge } from "@/components/StatusBadge";
import { ChefHat, Truck, ClipboardCheck, PackageCheck, IndianRupee, AlertCircle, CalendarDays, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="border-[#E8E3DF]" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#4B5563]">{label}</div>
            <div className="font-heading text-2xl text-[#141E30] mt-1">{value}</div>
            {sub && <div className="text-xs text-[#4B5563] mt-0.5">{sub}</div>}
          </div>
          <div className="h-9 w-9 rounded-md bg-[#F3E8E9] flex items-center justify-center">
            <Icon className="h-4 w-4 text-[#B86F77]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const nav = useNavigate();
  const { isOwner } = useAuth();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    const t = todayISO();
    api.get(`/orders?start=${t}&end=${t}`).then((r) => setToday(r.data));
    api.get(`/orders?start=${t}`).then((r) => setUpcoming(r.data.slice(0, 8)));
  }, []);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl text-[#141E30]">Dashboard</h1>
          <p className="text-sm text-[#4B5563]">Today is {fmtDate(todayISO())}</p>
        </div>
        <Button onClick={() => nav("/orders/new")} className="bg-[#B86F77] hover:bg-[#A35D65] h-9" data-testid="dashboard-new-order-btn" disabled={!isOwner} style={{ display: isOwner ? undefined : "none" }}>
          <Plus className="h-4 w-4 mr-1" /> New Order
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={CalendarDays} label="Today's Orders" value={stats?.todays_orders ?? "—"} testId="stat-today" />
        <Stat icon={ChefHat} label="In Production" value={stats?.in_production ?? "—"} testId="stat-in-prod" />
        <Stat icon={PackageCheck} label="Ready" value={stats?.ready ?? "—"} testId="stat-ready" />
        <Stat icon={Truck} label="Out for Delivery" value={stats?.out_for_delivery ?? "—"} testId="stat-out" />
        <Stat icon={ClipboardCheck} label="Confirmed" value={stats?.confirmed ?? "—"} testId="stat-confirmed" />
        <Stat icon={AlertCircle} label="New Inquiries" value={stats?.new_orders ?? "—"} testId="stat-new" />
        {isOwner && <Stat icon={IndianRupee} label="Revenue (Month)" value={stats ? inr(stats.month_revenue) : "—"} testId="stat-revenue" />}
        {isOwner && <Stat icon={IndianRupee} label="Pending Balance" value={stats ? inr(stats.month_pending) : "—"} sub="this month" testId="stat-pending" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#E8E3DF]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg">Today's Orders</h2>
              <Link to="/production" className="text-xs text-[#B86F77] hover:underline" data-testid="dash-prod-link">Open kitchen view →</Link>
            </div>
            <OrdersList orders={today} emptyMsg="No orders due today." />
          </CardContent>
        </Card>
        <Card className="border-[#E8E3DF]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg">Upcoming</h2>
              <Link to="/orders" className="text-xs text-[#B86F77] hover:underline" data-testid="dash-orders-link">All orders →</Link>
            </div>
            <OrdersList orders={upcoming} emptyMsg="No upcoming orders." />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OrdersList({ orders, emptyMsg }) {
  const { isOwner } = useAuth();
  if (!orders.length) return <div className="text-sm text-[#4B5563] py-6 text-center">{emptyMsg}</div>;
  return (
    <div className="divide-y divide-[#E8E3DF]">
      {orders.map((o) => (
        <Link
          key={o.id}
          to={`/orders/${o.id}`}
          data-testid={`dash-order-${o.order_id}`}
          className="flex items-center justify-between py-2.5 hover:bg-[#FCF9F9] -mx-2 px-2 rounded-md transition-colors"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{o.customer_name}</div>
            <div className="text-[11px] text-[#4B5563]">
              {o.order_id} · {fmtDate(o.required_date)} · {fmtTime(o.required_time)}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={o.status} />
            {isOwner && <div className="text-sm font-medium hidden sm:block">{inr(o.total_price)}</div>}
          </div>
        </Link>
      ))}
    </div>
  );
}
